import React from 'react'
import PageBase from 'components/page-title/PageBase'
import { AccessTime, AutoAwesome, AutoAwesomeSharp, AutoMode, Cancel, InfoOutlined, Insights, Search, SwapHorizRounded } from '@mui/icons-material'
import { Autocomplete, BottomNavigation, BottomNavigationAction, Box, Button, Card, CardActions, CardContent, Chip, CircularProgress, Collapse, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, List, ListItem, ListItemAvatar, ListItemText, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import PlayerImage from 'components/shared-components/PlayerImage'
import TeamImage from 'components/shared-components/TeamImage'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiQueryKeys, ApiQueryService } from 'services/query/apiQueryService'
import { type PlayerSearchResult } from 'features/trade-analyzer/model/playerSearchResult'
import { useDebounce, useDocumentTitle } from '@uidotdev/usehooks'
import { ApiMutationKeys, ApiMutationService } from 'services/mutation/apiMutationService'
import { type TradeReportDto } from 'features/trade-analyzer/model/tradeReportViewModel'
import PlayerFlag from 'components/my-fantasy-team/PlayerFlag'
import ComparativeAnalysis from './components/comparative-analysis/ComparativeAnalysis'
import TradeAnalysis from './components/trade-analysis/TradeAnalysis'
import XFactorDimension from './components/x-factor-dimension/XFactorDimension'
import TradeSuggestions from './components/trade-suggestions/TradeSuggestions'
import { type NavigateOptions, type SetURLSearchParams, type URLSearchParamsInit, useSearchParams } from 'react-router-dom'
import { TransitionGroup } from 'react-transition-group'

enum TradeEdgeBottomNavigationTabs {
  TradeEdge = 0,
  TradeSuggestions = 1
}

enum TradeAnalyzerSteps {
  SelectPlayers = 'SelectPlayers',
  AnalyzeTrade = 'AnalyzeTrade'
}

enum PlayerTradeSide {
  Trade = 'Trade',
  Receive = 'Receive'
}

interface PlayerSearchAutoCompleteProps {
  label?: string
  onPlayerSelectChange: (value: PlayerSearchResult, side: PlayerTradeSide) => void
}

const PlayerSearchAutoComplete: React.FC<PlayerSearchAutoCompleteProps> = ({
  label = 'Search for a player', onPlayerSelectChange: onChange
}) => {
  const [open, setOpen] = React.useState(false)
  const [players, setPlayers] = React.useState<readonly PlayerSearchResult[]>([])
  const [loading, setLoading] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState<string>('')
  const [key, setKey] = React.useState<string>('')
  const query = useDebounce(searchQuery.trim(), 500)
  const theme = useTheme()

  const { refetch: getPlayers } = useQuery<readonly PlayerSearchResult[]>({
    queryKey: [ApiQueryKeys.GetSearchForPlayersQuery, query],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetSearchForPlayersQuery](query)).data
    },
    enabled: open && query.trim().length > 2
  })

  React.useEffect(() => {
    const fetchPlayers = async (): Promise<void> => {
      if (open && query.trim().length > 2) {
        setLoading(true)
        const result = await getPlayers()
        setPlayers(result.data ?? [])
        setLoading(false)
      } else {
        setPlayers([])
      }
    }

    void fetchPlayers()
  }, [query, open, getPlayers])

  const setOptionForPlayer = React.useCallback((player: PlayerSearchResult, side: PlayerTradeSide) => {
    onChange(player, side)
    setKey(Date.now().toString())
    setPlayers([])
    setSearchQuery('')
    setOpen(false)
  }, [onChange])

  return (
    <Autocomplete
      sx={{
        width: '100%',
        maxWidth: 760,
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.3),
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.info.main
          }
        },
        '& label.Mui-focused': {
          color: theme.palette.info.main
        }
      }}
      open={open}
      onOpen={() => { setOpen(true) }}
      onClose={() => { setOpen(false) }}
      onInputChange={(_event, newValue) => { setSearchQuery(newValue) }}
      key={key}
      isOptionEqualToValue={(option, value) => option.playerId === value?.playerId}
      getOptionLabel={(option) => option.fullName}
      renderOption={(props, option) => (
        <ListItem
          {...props}
          component="li"
          key={option.playerId}
          disableGutters
          onClick={(event) => { event.preventDefault() }}
          sx={{ px: 1.5, py: 1 }}
        >
          <ListItemAvatar>
            <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
              <TeamImage height={48} width={48} src={option.teamLogoImageUrl} effect={'opacity'} />
              <PlayerImage height={44} width={44} src={option.headshotImageUrl} effect={'opacity'} />
            </Box>
          </ListItemAvatar>
          <Box mr={1} />
          <ListItemText
            primary={<Typography variant='subtitle2'>{option.fullName}</Typography>}
            secondary={<Typography variant='caption' color='text.secondary'>{option.position}</Typography>}
          />
          <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
            <Button size='small' variant='text' color='error' onClick={() => { setOptionForPlayer(option, PlayerTradeSide.Trade) }}>Trade</Button>
            <Button size='small' variant='text' color='success' onClick={() => { setOptionForPlayer(option, PlayerTradeSide.Receive) }}>Receive</Button>
          </Box>
        </ListItem>
      )}
      options={players}
      noOptionsText={query?.length > 2 ? 'No player found' : 'Type at least 3 characters to search players'}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
              <>
                {loading && <CircularProgress color='inherit' size={18} />}
                {params.InputProps.endAdornment}
              </>
              )
            }
          }}
        />
      )}
    />
  )
}

interface PlayersFromTeamForTradeProps {
  playersFromTeam: PlayerSearchResult[]
  title: string
  subtitle: string
  accentColor: string
  emptyMessage: string
  onPlayerRemove: (players: PlayerSearchResult[]) => void
  onPlayerSwitch: (player: PlayerSearchResult) => void
}

const PlayersFromTeamForTrade: React.FC<PlayersFromTeamForTradeProps> = ({
  playersFromTeam,
  title,
  subtitle,
  accentColor,
  emptyMessage,
  onPlayerRemove,
  onPlayerSwitch
}) => {
  const theme = useTheme()

  const renderPlayerCard = (player: PlayerSearchResult): JSX.Element => (
    <Card
      key={player.playerId}
      variant='outlined'
      sx={{
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        borderColor: alpha(accentColor, 0.35),
        background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ${alpha(accentColor, 0.12)} 100%)`
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.19,
          backgroundImage: `url(${player.teamLogoImageUrl})`,
          backgroundPosition: 'right -18px center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '52%',
          pointerEvents: 'none'
        }}
      />
      <CardContent sx={{ pb: 1, position: 'relative', zIndex: 1 }}>
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <Box
            sx={{
              position: 'relative',
              width: 72,
              height: 72,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PlayerImage height={72} width={72} src={player.headshotImageUrl} effect={'opacity'} />
          </Box>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant='subtitle2' noWrap fontWeight={700}>{player.fullName}</Typography>
            <Stack direction='row' spacing={0.75} mt={0.5}>
              <Chip size='small' label={player.position} sx={{ borderRadius: 1, bgcolor: alpha(accentColor, 0.2), fontWeight: 600 }} />
              <Chip size='small' color='secondary' label={`#${player.playerNumber}`} sx={{ borderRadius: 1, fontWeight: 600 }} />
            </Stack>
          </Box>

          <Stack alignItems='center' display={{ xs: 'none', sm: 'none', md: 'none', lg: 'flex' }} spacing={0.5}>
            <Box
              sx={{
                px: 0.75,
                py: 0.5,
                borderRadius: 1.25,
                border: `1px solid ${alpha(theme.palette.info.main, 0.45)}`,
                backgroundColor: alpha(theme.palette.info.main, 0.12),
                lineHeight: 0
              }}
            >
              <PlayerFlag country={player.country} />
            </Box>
            <Chip
              size='small'
              label={(player.country?.trim() ?? 'Country').toUpperCase()}
              sx={{
                borderRadius: 1,
                fontSize: '0.66rem',
                height: 22,
                bgcolor: alpha(theme.palette.info.main, 0.16),
                color: theme.palette.text.primary,
                fontWeight: 700
              }}
            />
          </Stack>
        </Stack>
      </CardContent>
      <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0.5, justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
        <Tooltip title='Remove player'>
          <Button
            size='small'
            variant='text'
            color='error'
            startIcon={<Cancel />}
            onClick={() => { onPlayerRemove(playersFromTeam.filter((p) => p.playerId !== player.playerId)) }}
          >
            Remove
          </Button>
        </Tooltip>
        <Tooltip title='Move to opposite side'>
          <Button
            size='small'
            variant='text'
            color='inherit'
            startIcon={<SwapHorizRounded />}
            onClick={() => { onPlayerSwitch(player) }}
          >
            Switch
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  )

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        borderRadius: 3,
        p: 2,
        borderColor: alpha(accentColor, 0.26),
        minHeight: 360,
        background: `linear-gradient(165deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(accentColor, 0.08)} 100%)`
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Box>
          <Typography variant='h6' sx={{ lineHeight: 1.1 }}>{title}</Typography>
          <Typography variant='caption' color='text.secondary'>{subtitle}</Typography>
        </Box>
        <Chip
          label={`${playersFromTeam.length} selected`}
          size='small'
          sx={{ fontWeight: 600, bgcolor: alpha(accentColor, 0.2), color: theme.palette.text.primary }}
        />
      </Stack>
      <Divider sx={{ my: 1.5, borderColor: alpha(accentColor, 0.25) }} />

      {playersFromTeam.length === 0 && (
        <Box
          sx={{
            minHeight: 210,
            borderRadius: 2,
            border: `1px dashed ${alpha(accentColor, 0.45)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2
          }}
        >
          <Typography variant='body2' color='text.secondary' textAlign='center'>{emptyMessage}</Typography>
        </Box>
      )}

      <Stack spacing={1.25}>
        <TransitionGroup>
          {playersFromTeam.map(player => <Collapse key={player.playerId}>{renderPlayerCard(player)}</Collapse>)}
        </TransitionGroup>
      </Stack>
    </Paper>
  )
}

const TradeEdge: React.FC = () => {
  useDocumentTitle('FantaHockey | Trade Edge')

  const [playersToTrade, setPlayersToTrade] = React.useState<PlayerSearchResult[]>([])
  const [playersToReceive, setPlayersToReceive] = React.useState<PlayerSearchResult[]>([])

  const [tradeAnalyzerStep, setTradeAnalyzerStep] = React.useState<TradeAnalyzerSteps>(TradeAnalyzerSteps.SelectPlayers)
  const [tradeReport, setTradeReport] = React.useState<TradeReportDto | undefined>(undefined)
  const [openInfoDialog, setOpenInfoDialog] = React.useState(false)
  const [value, setValue] = React.useState<TradeEdgeBottomNavigationTabs>(TradeEdgeBottomNavigationTabs.TradeEdge)
  const ref = React.useRef<HTMLDivElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()

  const { isPending: isLoadingTrade, mutateAsync: generateTradeReportAsync } = useMutation<TradeReportDto>({
    mutationKey: [ApiMutationKeys.BuildFantasyHockeyTradeReport],
    mutationFn: async () => {
      return (await ApiMutationService[ApiMutationKeys.BuildFantasyHockeyTradeReport](
        playersToReceive,
        playersToTrade
      )).data
    }
  })

  React.useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'trade-suggestions') {
      setValue(TradeEdgeBottomNavigationTabs.TradeSuggestions)
    } else if (tab === 'trade-edge') {
      setValue(TradeEdgeBottomNavigationTabs.TradeEdge)
    }
  }, [searchParams])

  React.useEffect(() => {
    setValue(TradeEdgeBottomNavigationTabs.TradeEdge)
    const fetchTradeReport = async (): Promise<void> => {
      switch (tradeAnalyzerStep) {
        case TradeAnalyzerSteps.SelectPlayers:
          setPlayersToReceive([])
          setPlayersToTrade([])
          setTradeReport(undefined)
          break
        case TradeAnalyzerSteps.AnalyzeTrade: {
          if (playersToTrade.length > 0 && playersToReceive.length > 0) {
            const report = await generateTradeReportAsync()
            setTradeReport(report)
          }
          break
        }
      }
    }
    void fetchTradeReport()
  }, [tradeAnalyzerStep, generateTradeReportAsync])

  const allPlayersSelected = React.useMemo(() => playersToTrade.length > 0 && playersToReceive.length > 0, [playersToTrade, playersToReceive])

  const handleClose = (): void => {
    setOpenInfoDialog(false)
  }

  const handlePlayerSelectChange = (player: PlayerSearchResult, side: PlayerTradeSide): void => {
    const alreadySelected =
      playersToTrade.some((p) => p.playerId === player.playerId) ||
      playersToReceive.some((p) => p.playerId === player.playerId)

    if (alreadySelected) {
      return
    }

    if (side === PlayerTradeSide.Trade) {
      setPlayersToTrade((prev) => [...prev, player])
    } else {
      setPlayersToReceive((prev) => [...prev, player])
    }
  }

  const handlePlayerRemove = (updatedPlayers: PlayerSearchResult[], side: PlayerTradeSide): void => {
    if (side === PlayerTradeSide.Trade) {
      setPlayersToTrade(updatedPlayers)
    } else {
      setPlayersToReceive(updatedPlayers)
    }
  }

  const handlePlayerSwitch = (player: PlayerSearchResult, side: PlayerTradeSide): void => {
    if (side === PlayerTradeSide.Trade) {
      setPlayersToReceive((prev) => [...prev, player])
      setPlayersToTrade((prev) => prev.filter((p) => p.playerId !== player.playerId))
    } else {
      setPlayersToTrade((prev) => [...prev, player])
      setPlayersToReceive((prev) => prev.filter((p) => p.playerId !== player.playerId))
    }
  }

  const updateQuery: SetURLSearchParams = (nextInit: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit) | undefined, navigateOpts: NavigateOptions | undefined) => {
    setSearchParams(nextInit, navigateOpts)
  }
  const handleUpdateWithCallback = (tab: string): void => {
    updateQuery((prev: string | URLSearchParams | string[][] | Record<string, string> | undefined) => {
      const newParams = new URLSearchParams(prev)
      newParams.set('tab', tab)
      return newParams
    })
  }

  return (
    <PageBase icon={AutoMode} title="Trade Edge">
      {value === TradeEdgeBottomNavigationTabs.TradeEdge && (
      <Box p={{ xs: 1, md: 2 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 1.5, md: 2.5 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
            background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.primary.dark, 0.86)} 100%)`
          }}
        >
          {tradeAnalyzerStep === TradeAnalyzerSteps.SelectPlayers && (
            <Stack spacing={2.25}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Build both sides of your trade, then run a full analysis in one click.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button variant="outlined" color='info' startIcon={<InfoOutlined />} onClick={() => { setOpenInfoDialog(true) }}>
                    How It Works
                  </Button>
                </Stack>
              </Stack>

              <TradeEdgeAiInfoDialog
                open={openInfoDialog}
                onClose={handleClose}
              />

              <PlayerSearchAutoComplete
                label='Search players and assign side'
                onPlayerSelectChange={handlePlayerSelectChange}
              />

              <Grid container spacing={1.5} alignItems="stretch">
                <Grid item xs={12} md={5.5}>
                  <PlayersFromTeamForTrade
                    title='Trading Away'
                    subtitle='Assets leaving your roster'
                    accentColor={theme.palette.error.main}
                    emptyMessage='Add players here from search results using the Trade action.'
                    playersFromTeam={playersToTrade}
                    onPlayerRemove={(updated) => { handlePlayerRemove(updated, PlayerTradeSide.Trade) }}
                    onPlayerSwitch={(player) => { handlePlayerSwitch(player, PlayerTradeSide.Trade) }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={1}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      border: `1px solid ${alpha(theme.palette.info.main, 0.5)}`,
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: alpha(theme.palette.info.main, 0.12)
                    }}
                  >
                    <SwapHorizRounded />
                  </Box>
                </Grid>

                <Grid item xs={12} md={5.5}>
                  <PlayersFromTeamForTrade
                    title='Receiving'
                    subtitle='Assets joining your roster'
                    accentColor={theme.palette.success.main}
                    emptyMessage='Add players here from search results using the Receive action.'
                    playersFromTeam={playersToReceive}
                    onPlayerRemove={(updated) => { handlePlayerRemove(updated, PlayerTradeSide.Receive) }}
                    onPlayerSwitch={(player) => { handlePlayerSwitch(player, PlayerTradeSide.Receive) }}
                  />
                </Grid>

                <Grid item xs={12} my={1}>
                  <Stack alignItems='center' spacing={1} sx={{ mt: 1 }}>
                    <Button
                      sx={{ px: 2.5, py: 1 }}
                      variant="contained"
                      color='success'
                      disabled={!allPlayersSelected}
                      startIcon={<Insights />}
                      onClick={() => {
                        setTradeAnalyzerStep(TradeAnalyzerSteps.AnalyzeTrade)
                      }}
                    >
                      Analyze Trade
                    </Button>
                    {!allPlayersSelected && (
                      <Typography variant='caption' color='text.secondary'>
                        Select at least one player on each side to continue.
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          )}
          {/* Trade Analysis */}
          {tradeAnalyzerStep === TradeAnalyzerSteps.AnalyzeTrade && (
            <Box
              p={{ xs: 1, md: 2 }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              {/* Loading */}
              {isLoadingTrade && (
                <Paper
                  variant='outlined'
                  sx={{
                    maxWidth: 560,
                    mx: 'auto',
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    borderColor: alpha(theme.palette.info.main, 0.4),
                    textAlign: 'center'
                  }}
                >
                  <Stack spacing={1.25} alignItems='center'>
                    <CircularProgress color='info' size={34} />
                    <Typography variant='h6' fontWeight={700}>Building your trade report</Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ display: 'flex', alignItems: 'center' }}>
                      <AutoAwesome sx={{ mr: 0.75, color: 'text.primary' }} fontSize='small' />
                      Running statistical and contextual analysis
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTime sx={{ mr: 0.75, color: 'text.primary' }} fontSize='small' />
                      This can take up to a minute for larger trades
                    </Typography>
                  </Stack>
                </Paper>
              )}

              {/* Trade Report */}
              {tradeReport != null && !isLoadingTrade && (
                <Box sx={{ width: '100%', maxWidth: 1040 }}>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: { xs: 1.5, md: 2 },
                      mb: 2,
                      borderRadius: 2.5,
                      borderColor: alpha(theme.palette.common.white, 0.12),
                      background: alpha(theme.palette.background.paper, 0.65)
                    }}
                  >
                    <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 1 }}>
                      Trade Analysis Overview
                    </Typography>
                    <Typography variant='body2'>
                      {tradeReport.statisticalAnalysis.analysisText}
                    </Typography>
                  </Paper>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2, mb: 1 }}>
                      {playersToTrade.length > 1 ? 'Players' : 'Player'} To Trade
                      </Typography>
                      {tradeReport.tradeDetails.incomingPlayersToTeamTradingAway.map(
                        (p) => (
                          <List
                            key={p.fullName}
                            sx={{ width: '100%', mb: 1 }}
                          >
                            <ListItem>
                              <ListItemAvatar>
                                <PlayerImage height={48} width={48} src={p.headshotImageUrl} />
                              </ListItemAvatar>
                              <ListItemText primary={p.fullName} secondary={p.position} />
                              <ListItemAvatar>
                                <TeamImage height={48} width={48} src={p.teamLogoImageUrl} />
                              </ListItemAvatar>
                            </ListItem>
                          </List>
                        )
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2, mb: 1 }}>
                      {playersToReceive.length > 1 ? 'Players' : 'Player'} To Receive
                      </Typography>
                      {tradeReport.tradeDetails.outgoingPlayersFromTeamReceiving.map(
                        (p) => (
                          <List
                            key={p.fullName}
                            sx={{ width: '100%', mb: 1 }}
                          >
                            <ListItem>
                              <ListItemAvatar>
                                <PlayerImage height={48} width={48} src={p.headshotImageUrl} />
                              </ListItemAvatar>
                              <ListItemText primary={`${p.fullName}`} secondary={p.position} />
                              <ListItemAvatar>
                                <TeamImage height={48} width={48} src={p.teamLogoImageUrl} />
                              </ListItemAvatar>
                            </ListItem>
                          </List>
                        )
                      )}
                    </Grid>
                  </Grid>
                  {tradeReport.statisticalAnalysis.playerMetricsTable?.length > 0 && <> <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                  Player Statistics
                  </Typography>
                    <Box mt={2} />
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 320 }} aria-label="trade-analysis">
                        <TableHead sx={{ backgroundColor: 'secondary.main' }}>
                          <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Player Name</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Goals</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assists</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Points</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>+/-</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PP Points</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SOG</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>CF%</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>FF%</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>xGoals</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Games Played</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Game Score</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Faceoffs Won</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Faceoffs Lost</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PIM</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Blocks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tradeReport.statisticalAnalysis.playerMetricsTable.map(
                            (row) => (
                              <TableRow
                                key={row.playerName}
                                sx={{
                                  '&:last-child td, &:last-child th': { border: 0 }
                                }}
                              >
                                <TableCell component="th" scope="row">
                                  {row.playerName}
                                </TableCell>
                                <TableCell align="center">{row.goals}</TableCell>
                                <TableCell align="center">{row.assists}</TableCell>
                                <TableCell align="center">{row.points}</TableCell>
                                <TableCell align="center">{row.plusMinus}</TableCell>
                                <TableCell align="center">
                                  {row.powerPlayPoints ?? 'N/A'}
                                </TableCell>
                                <TableCell align="center">{row.shotsOnGoal ?? 'N/A'}</TableCell>
                                <TableCell align="center">
                                  {row.corsiForPercentage ?? 'N/A'}
                                </TableCell>
                                <TableCell align="center">
                                  {row.fenwickForPercentage ?? 'N/A'}
                                </TableCell>
                                <TableCell align="center">{row.xGoals ?? 'N/A'}</TableCell>
                                <TableCell align="center">{row.gamesPlayed}</TableCell>
                                <TableCell align="center">{row.gameScore}</TableCell>
                                <TableCell align="center">{row.faceoffsWon}</TableCell>
                                <TableCell align="center">{row.faceoffsLost}</TableCell>
                                <TableCell align="center">{row.penaltyMinutes}</TableCell>
                                <TableCell align="center">{row.shotsBlockedByPlayer}</TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer> </>}
                  {tradeReport.statisticalAnalysis.goalieMetricsTable?.length > 0 && <> <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                  Goalie Statistics
                  </Typography>
                    <Box mt={2} />
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 320 }} aria-label="trade-analysis">
                        <TableHead sx={{ backgroundColor: 'secondary.main' }}>
                          <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Player Name</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Team</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Wins</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Losses</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ice Time</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>GAA</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SV %</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Shutouts</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tradeReport.statisticalAnalysis.goalieMetricsTable.map(
                            (row) => (
                              <TableRow
                                key={row.playerName}
                                sx={{
                                  '&:last-child td, &:last-child th': { border: 0 }
                                }}
                              >
                                <TableCell component="th" scope="row">
                                  {row.playerName}
                                </TableCell>
                                <TableCell align="center">{row.team}</TableCell>
                                <TableCell align="center">{row.wins}</TableCell>
                                <TableCell align="center">{row.losses}</TableCell>
                                <TableCell align="center">{row.iceTime}</TableCell>
                                <TableCell align="center">{row.goalsAgainstAverage}</TableCell>
                                <TableCell align="center">
                                  {row.savePercentage}
                                </TableCell>
                                <TableCell align="center">{row.shutouts}</TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>}
                  <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                   Multi-Player Dynamics
                  </Typography>
                  <Typography my={2}>
                    {tradeReport.multiPlayerDynamics?.impactOnRoster}
                  </Typography>
                  <Typography my={2}>
                    {tradeReport.multiPlayerDynamics?.positionalBalance}
                  </Typography>
                  <Typography my={2}>
                    {tradeReport.multiPlayerDynamics?.categoryCoverage}
                  </Typography>
                  <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                    X-Factor Dimension
                  </Typography>
                  <Box mt={2} />
                  <XFactorDimension xFactorDimensions={tradeReport.xFactorDimension} />
                  <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                   Comparative Analysis
                  </Typography>
                  <ComparativeAnalysis comparativeAnalysis={tradeReport.comparativeAnalysis} />
                  <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                   Yahoo Fantasy Context
                  </Typography>
                  <Typography my={2}>
                    {tradeReport.yahooFantasyContext.information}
                  </Typography>
                  <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                   Trade Player Scores
                  </Typography>
                  <Typography my={2}>
                    The following table shows the player scores based on advanced performance trends, fantasy points, historical reliability, league settings fit, and x-factor intangibles.
                  </Typography>
                  <TradeAnalysis pointsTable={tradeReport.scoringSystem.pointsTable} />
                  <Typography fontWeight={700} color="textPrimary" sx={{ mt: 2 }}>
                   Summary
                  </Typography>
                  <Typography my={2}>
                    {tradeReport.finalDetermination.summary}
                  </Typography>
                  <Box mt={4} display="flex" justifyContent="center">
                    <Button
                      sx={{ p: 1 }}
                      variant="outlined"
                      color="success"
                      onClick={() => {
                        setTradeAnalyzerStep(TradeAnalyzerSteps.SelectPlayers)
                      }}
                    >
                      <SwapHorizRounded />
                      <Box ml={1} />
                      Start a New Trade
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>
      )}
      {/* Trade Suggestions */}
      {value === TradeEdgeBottomNavigationTabs.TradeSuggestions && (
        <Box p={{ xs: 1, md: 2 }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 1, md: 2 },
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`
            }}
          >
            <Box>
              <TradeSuggestions />
            </Box>
          </Paper>
        </Box>
      )}
      <Box sx={{ pt: 1, pb: 8 }} ref={ref}>
        <CssBaseline />
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 0,
            borderTop: `1px solid ${alpha(theme.palette.common.white, 0.12)}`
          }}
          elevation={3}
        >
          <BottomNavigation
            showLabels
            value={value}
            onChange={(_event, newValue: number) => {
              setValue(newValue)
              handleUpdateWithCallback(TradeEdgeBottomNavigationTabs.TradeEdge === newValue ? 'trade-edge' : 'trade-suggestions')
            }}
          >
            <BottomNavigationAction label="Trade Edge" icon={<AutoMode />} />
            <BottomNavigationAction label="Trade Suggestions" icon={<AutoAwesomeSharp />} />
          </BottomNavigation>
        </Paper>
      </Box>
    </PageBase>
  )
}

export interface TradeEdgeAiInfoDialogProps {
  open: boolean
  onClose: () => void
}

const TradeEdgeAiInfoDialog: React.FC<TradeEdgeAiInfoDialogProps> = ({ open, onClose }) => {
  const handleClose = (): void => {
    onClose()
  }

  return (
    <Dialog onClose={handleClose} open={open} maxWidth='sm' fullWidth>
      <DialogTitle>How Trade Edge Works</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant='body2'>
            Trade Edge evaluates player performance, trend context, fantasy scoring fit and intangibles to estimate trade value.
          </Typography>
          <Typography variant='body2'>
            Search players, assign each one to Trade or Receive, then run Analyze Trade to generate the report.
          </Typography>
          <Typography variant='body2'>
            You can remove or switch players at any time. For the cleanest signal, keep most packages to 1-3 players per side.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant='text' color='inherit'>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default React.memo(TradeEdge)
